import React, { useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { FolderMinus, FolderPlus, RotateCcw, Trash2 } from 'lucide-react'
import { useContentData } from '../hooks/UseContentData'
import { usePlaybooksData } from '../hooks/UsePlaybooksData'
import { useTeamsData } from '../hooks/UseTeamsData'
import { createDocument as apiCreateDocument } from '../api/queries/DocumentQueries'
import { blockKeys } from '../api/queries/BlockQueries'
import { useFoldersData } from '../hooks/UseFoldersData'
import { useModalManager } from '@/hooks'
import { usePrefetchSections } from '@/hooks'
import { useContentManagerConfirmations } from '@/pages/content-manager'
import { useContentCardCallbacks, type ContentItem as CardContentItem } from './content-manager/hooks/UseContentCardCallbacks'
import { useContentManagerState } from '@/pages/content-manager'
import { ContentSidebar } from '../components/content-manager/ContentSidebar'
import { ContentToolbar } from '../components/content-manager/ContentToolbar'
import { ContentCard } from '../components/content-manager/ContentCard'
import { ContentListHeader, ContentListRow } from '../components/content-manager/ContentListRow'
import { WindowedContentGrid } from '../components/content-manager/WindowedContentGrid'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../components/ui/Dialog'
import { Button } from '../components/ui/Button'
import { Loading } from '../components/ui/Loading'
import { UnifiedSettingsDialog } from '../components/shared/UnifiedSettingsDialog'
import { SelectionActionBar } from '../components/shared/SelectionActionBar'
import { ErrorBoundary } from '../components/shared/ErrorBoundary'
import { ManageTeamsDialog } from '../components/content-manager/ManageTeamsDialog'
import { CANVAS_ROOT_TYPES, type DocumentTemplateType, type CreateDocumentSectionRequest } from '@/types/blocks'
import { NewFolderDialog } from '../components/content-manager/NewFolderDialog'
import { NewPlaybookDialog } from '../components/content-manager/NewPlaybookDialog'
import { NewDocumentDialog } from '../components/content-manager/NewDocumentDialog'
import { AddToFolderDialog } from '../components/content-manager/AddToFolderDialog'
import { PlaybookShareDialog } from '../components/shared/PlaybookShareDialog'
import { RenameDialog } from '../components/content-manager/RenameDialog'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '../components/ui/Resizable'
import { MAX_SIDEBAR_WIDTH, MIN_SIDEBAR_WIDTH, useSettings } from '../contexts/SettingsContext'
import { useOnboarding } from '../contexts/OnboardingContext'
import { TourController } from '@/components/onboarding'
import { WelcomeModal } from '@/components/onboarding'
import { TRASH_RETENTION_DAYS } from '../constants/Trash'
import { BASELINE_VIEWPORT } from '@/constants'
import type { ContentType } from '@/types/Content'
import type { ContentType as DbContentType } from '../db/Types'
import { secureJsonFetch } from '@/utils/SecureFetch'
import './content-manager-page.css'

export function ContentManagerPage() {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { sidebarWidth, setSidebarWidth } = useSettings()
	const { state, startTour, markWelcomeSeen } = useOnboarding()

	// Consolidated UI state (section, folder, search, selection, share/rename targets)
	const {
		activeSection,
		setActiveSection,
		selectedFolderId,
		setSelectedFolderId,
		searchQuery,
		setSearchQuery,
		viewMode,
		setViewMode,
		selectedItems,
		toggleSelection,
		isItemSelected,
		clearSelection,
		shareTarget,
		setShareTarget,
		renameTarget,
		setRenameTarget,
	} = useContentManagerState()

	// Onboarding tour state
	const showWelcome = !state.hasSeenWelcome && !state.completedTours.includes('content-manager')

	// Modal management with useModalManager
	const modals = useModalManager([
		'newPlaybook',
		'newDocument',
		'settings',
		'manageTeams',
		'newFolder',
		'share',
		'addToFolder',
		'rename',
	] as const)

	// Data hooks - must come before confirmation hooks that use their functions
	const { teams, currentTeamId, switchTeam, isLoading: teamsLoading } = useTeamsData()

	// Prefetch sections for instant navigation
	const { prefetchSection } = usePrefetchSections(currentTeamId)

	// Fetch unified content (playbooks + blocks)
	const {
		content,
		isLoading: contentLoading,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		blockOperations,
	} = useContentData({
		section: activeSection,
		folderId: selectedFolderId,
		teamId: currentTeamId,
	})

	// Playbook-specific mutations
	const {
		playbooks,
		createPlaybook,
		updatePlaybook,
		deletePlaybook,
		toggleStar,
		restore,
		permanentDelete,
		emptyTrash,
		error: playbooksError,
	} = usePlaybooksData(currentTeamId, activeSection, selectedFolderId)

	// Document operations: create via V2 API, delete/restore via blockOperations
	// Note: DocumentTemplateType from UI is visual template, DocumentType is storage format
	// For now, default to 'call_sheet' as the document format
	const createDocument = async (data: {
		playbook_id: string
		name: string
		description?: string
		template_type?: DocumentTemplateType
		sections?: CreateDocumentSectionRequest[]
	}) => {
		return apiCreateDocument(data.playbook_id, {
			name: data.name,
			template_type: 'call_sheet', // Default document format
			description: data.description,
			sections: data.sections?.map(s => ({ name: s.name, color: s.color })),
		})
	}

	const {
		folders,
		isLoading: foldersLoading,
		error: foldersError,
		updateFolder,
		deleteFolder,
	} = useFoldersData()

	// Clear selected folder if it no longer exists (e.g. deleted)
	useEffect(() => {
		if (selectedFolderId && !foldersLoading && !folders.some(f => f.id === selectedFolderId)) {
			setSelectedFolderId(null)
		}
	}, [folders, foldersLoading, selectedFolderId, setSelectedFolderId])

	// Derive selected folder name for toolbar context
	const selectedFolderName = useMemo(() => {
		if (!selectedFolderId) return null
		return folders.find(f => f.id === selectedFolderId)?.name ?? null
	}, [selectedFolderId, folders])

	// Helper to check if a type is a block type (includes documents now)
	const isBlockType = (type: string): type is 'play' | 'formation' | 'concept' | 'document' =>
		[...CANVAS_ROOT_TYPES, 'document'].includes(type)

	// Confirmation dialogs - using extracted hook for cleaner organization
	// Note: All IDs are string UUIDs
	const confirmations = useContentManagerConfirmations({
		onDeleteItem: async (item) => {
			if (isBlockType(item.type)) {
				await blockOperations.trash(item.id)
			} else {
				await deletePlaybook(item.id)
			}
		},
		onPermanentDelete: async (item) => {
			if (isBlockType(item.type)) {
				await blockOperations.permanentDelete(item.id)
			} else {
				await permanentDelete(item.id)
			}
		},
		onRestore: async (item) => {
			if (isBlockType(item.type)) {
				await blockOperations.restore(item.id)
			} else {
				await restore(item.id)
			}
		},
		onEmptyTrash: async () => {
			await emptyTrash()
		},
		onDeleteFolder: async (folder) => {
			await deleteFolder(folder.id)
		},
	})

	const isLoading = contentLoading || teamsLoading || foldersLoading
	const error = playbooksError || foldersError

	// Filter content by search query
	const filteredContent = useMemo(
		() => content.filter(item =>
			(item.name ?? '').toLowerCase().includes(searchQuery.toLowerCase()),
		),
		[content, searchQuery],
	)

	const handleCreatePlaybook = useCallback(async (name: string) => {
		// Use current team or default to first available team (user's "My Team")
		const teamId = currentTeamId ?? teams[0]?.id ?? null
		const newPlaybook = await createPlaybook(name, teamId)
		navigate(`/playbooks/${newPlaybook.id}`)
	}, [currentTeamId, teams, createPlaybook, navigate])

	const handleCreateDocument = useCallback(async (
		playbookId: string,
		name: string,
		templateType: DocumentTemplateType,
		sections: CreateDocumentSectionRequest[],
		description?: string,
	) => {
		try {
			const newDocument = await createDocument({
				playbook_id: playbookId,
				name,
				template_type: templateType,
				sections,
				description,
			})
			// Invalidate block cache so document appears in Content Manager on return
			await queryClient.invalidateQueries({ queryKey: blockKeys.lists() })
			// Navigate to the document editor
			navigate(`/documents/${newDocument.id}/edit`)
		} catch (error) {
			console.error('Failed to create document:', error)
			toast.error('Failed to create document. Please try again.')
		}
	}, [createDocument, queryClient, navigate])

	const handleDuplicate = async (id: string) => {
		const original = filteredContent.find(item => item.id === id)
		if (!original) return

		if (original.type === 'playbook') {
			await createPlaybook(`${original.name} (Copy)`, original.teamId ?? null)
		}
	}

	const handleNewFolder = () => {
		modals.open('newFolder')
	}

	const handleImport = () => {
		// TODO: Implement import functionality
	}

	const handleExport = () => {
		// TODO: Implement export functionality
	}

	const handleSettings = () => {
		modals.open('settings')
	}

	const handleManageTeams = () => {
		modals.open('manageTeams')
	}

	const handleShare = useCallback((id: string) => {
		const item = filteredContent.find(item => item.id === id)
		if (!item) return

		if (item.type === 'playbook') {
			setShareTarget({ id, name: item.name, teamId: item.teamId ?? null })
			modals.open('share')
		} else if (item.type === 'document' && item.sourcePlaybookId) {
			// Documents inherit sharing from their parent playbook
			setShareTarget({
				id: item.sourcePlaybookId,
				name: item.sourcePlaybookName ?? 'Playbook',
				teamId: item.teamId ?? null
			})
			modals.open('share')
		}
	}, [filteredContent, modals])

	const handleExportPlaybook = (_id: string) => {
		// TODO: Implement single playbook export
	}

	const handleToggleStar = async (id: string) => {
		await toggleStar(id)
	}

	const handleRenamePrompt = useCallback((id: string, currentName: string) => {
		setRenameTarget({
			id,
			name: currentName,
			type: 'playbook',
		})
		modals.open('rename')
	}, [modals])

	// Convert to CardContentItem format for the callbacks hook
	const cardItems: CardContentItem[] = useMemo(
		() => filteredContent.map(item => ({
			id: item.id,
			name: item.name,
			type: item.type,
			playCount: item.playCount,
			sectionCount: item.sectionCount,
			updatedAt: item.updatedAt,
			starred: item.starred,
		})),
		[filteredContent],
	)

	// Use stable card callbacks hook - eliminates inline arrow functions per item
	const cardCallbacks = useContentCardCallbacks({
		activeSection,
		items: cardItems,
		confirmations,
		actions: {
			handleRenamePrompt,
			handleShare,
			handleToggleStar,
		},
		isItemSelected,
		toggleSelection,
	})

	// Block types that should use blockOperations (uses shared constant)
	// Note: CANVAS_ROOT_TYPES values are a subset of ContentType
	const BLOCK_CONTENT_TYPES = CANVAS_ROOT_TYPES as readonly ('play' | 'formation' | 'concept')[]

	// Generic batch action processor - eliminates DRY violation
	const processBatchAction = useCallback(async (
		playbookFn: (id: string) => Promise<unknown>,
		documentFn: (id: string) => Promise<unknown>,
		blockFn?: (id: string) => Promise<unknown>,
	) => {
		const items = Array.from(selectedItems).map(key => {
			const [type, id] = key.split(':') as [ContentType, string]
			return { type, id }
		})

		await Promise.allSettled(items.map(item => {
			switch (item.type) {
				case 'playbook':
					return playbookFn(item.id)
				case 'document':
					return documentFn(item.id)
				case 'play':
				case 'formation':
				case 'concept':
					return blockFn ? blockFn(item.id) : Promise.resolve()
				default:
					return Promise.resolve()
			}
		}))

		clearSelection()
	}, [selectedItems, clearSelection])

	const handleBatchRestore = useCallback(async () => {
		// All IDs are string UUIDs
		await processBatchAction(
			(id) => restore(id),
			(id) => blockOperations.restore(id),
			(id) => blockOperations.restore(id)
		)
	}, [processBatchAction, restore, blockOperations.restore])

	const handleBatchPermanentDelete = useCallback(async () => {
		// All IDs are string UUIDs
		await processBatchAction(
			(id) => permanentDelete(id),
			(id) => blockOperations.permanentDelete(id),
			(id) => blockOperations.permanentDelete(id)
		)
	}, [processBatchAction, permanentDelete, blockOperations.permanentDelete])

	const handleBatchDelete = useCallback(async () => {
		// All IDs are string UUIDs
		await processBatchAction(
			(id) => deletePlaybook(id),
			(id) => blockOperations.trash(id),
			(id) => blockOperations.trash(id)
		)
	}, [processBatchAction, deletePlaybook, blockOperations.trash])

	const handleAddToFolders = useCallback(async (folderIds: string[], items: Array<{
		type: ContentType;
		id: string
	}>) => {
		// Add items to each selected folder
		await Promise.allSettled(
			folderIds.map(async (folderId) => {
				const response = await secureJsonFetch(`/api/folders/${folderId}/contents`, {
					method: 'POST',
					body: { items },
				})
				if (!response.ok) throw new Error('Failed to add items to folder')
			}),
		)
		clearSelection()
	}, [clearSelection])

	const handleRemoveFromFolder = useCallback(async () => {
		if (selectedFolderId === null) return

		const items = Array.from(selectedItems).map(key => {
			const [type, id] = key.split(':') as [ContentType, string]
			return { type, id }
		})

		const response = await secureJsonFetch(`/api/folders/${selectedFolderId}/contents`, {
			method: 'DELETE',
			body: { items },
		})

		if (!response.ok) throw new Error('Failed to remove items from folder')

		clearSelection()
	}, [selectedFolderId, selectedItems, clearSelection])

	const handleRenameFolder = useCallback((folderId: string, currentName: string) => {
		setRenameTarget({
			id: folderId,
			name: currentName,
			type: 'folder',
		})
		modals.open('rename')
	}, [modals])

	// Handle rename from RenameDialog
	const handleRename = useCallback(async (id: string | number, newName: string) => {
		if (!renameTarget) return

		// All IDs are UUID strings - no conversion needed
		const idStr = typeof id === 'string' ? id : id.toString()
		if (renameTarget.type === 'playbook') {
			await updatePlaybook(idStr, { name: newName })
		} else if (renameTarget.type === 'folder') {
			await updateFolder(idStr, newName)
		}
	}, [renameTarget, updatePlaybook, updateFolder])

	const handleDeleteFolder = useCallback((folderId: string, name: string) => {
		confirmations.deleteFolderConfirm.prompt({ id: folderId, name })
	}, [confirmations.deleteFolderConfirm])

	if (isLoading) {
		return <Loading fullscreen />
	}

	if (error) {
		return (
			<div className="page-error">
				<div className="page-error-content">
					<h1 className="page-error-title" data-variant="error">Error</h1>
					<p className="page-error-message">{error}</p>
				</div>
			</div>
		)
	}

	// Calculate initial size as percentage based on baseline viewport
	const initialSidebarPercent = (sidebarWidth / BASELINE_VIEWPORT) * 100

	return (
		<>
			<ResizablePanelGroup
				direction="horizontal"
				className="content-manager-page"
			>
				<ResizablePanel
					id="sidebar"
					defaultSize={initialSidebarPercent}
					minSize={(MIN_SIDEBAR_WIDTH / BASELINE_VIEWPORT) * 100}
					maxSize={(MAX_SIDEBAR_WIDTH / BASELINE_VIEWPORT) * 100}
					onResize={(size) => {
						// Convert percentage back to pixels
						const width = Math.round((size / 100) * BASELINE_VIEWPORT)
						setSidebarWidth(width)
					}}
				>
					<ContentSidebar
						activeSection={activeSection}
						onSectionChange={setActiveSection}
						onSectionHover={prefetchSection}
						folders={folders}
						isFoldersLoading={foldersLoading}
						selectedFolderId={selectedFolderId}
						onFolderSelect={setSelectedFolderId}
						onAddFolder={() => modals.open('newFolder')}
						onRenameFolder={handleRenameFolder}
						onDeleteFolder={handleDeleteFolder}
					/>
				</ResizablePanel>

				{/*
			  Keyboard accessible resize handle:
			  - Tab to focus the handle
			  - Arrow keys (←/→) to resize
			  - Home/End to jump to min/max
			  - Enter to toggle expand/collapse
			*/}
				<ResizableHandle
					className="content-manager-resize-handle"
				/>

				<ResizablePanel defaultSize={100 - initialSidebarPercent} minSize={50}>
					<ErrorBoundary
						fallback={
							<div className="page-error">
								<div className="page-error-content">
									<h1 className="page-error-title" data-variant="error">Something went wrong</h1>
									<p className="page-error-message">
										An error occurred while loading the content. Please try refreshing the page.
									</p>
									<button
										onClick={() => window.location.reload()}
										className="empty-state-button"
									>
										Reload Page
									</button>
								</div>
							</div>
						}
					>
					<div className="content-manager-content">
						{/* Toolbar */}
						<ContentToolbar
							activeSection={activeSection}
							selectedFolderName={selectedFolderName}
							viewMode={viewMode}
							onViewModeChange={setViewMode}
							searchQuery={searchQuery}
							onSearchChange={setSearchQuery}
							onNewPlaybook={() => modals.open('newPlaybook')}
							onNewDocument={() => modals.open('newDocument')}
							onNewFolder={handleNewFolder}
							onImport={handleImport}
							onExport={handleExport}
							onSettingsClick={handleSettings}
							teams={teams}
							currentTeamId={currentTeamId}
							onSwitchTeam={switchTeam}
							onManageTeams={handleManageTeams}
						/>

						{/* Content */}
						<div className="content-manager-content-inner" data-tour-id="content-grid">
							{/* Trash notice */}
							{activeSection === 'trash' && filteredContent.length > 0 && (
								<div className="trash-header">
									<p className="trash-notice">
										Items in trash will be permanently deleted
										after {TRASH_RETENTION_DAYS} days
									</p>
									<button
										onClick={() => confirmations.emptyTrashConfirm.prompt({})}
										className="trash-empty-button"
									>
										Empty Trash
									</button>
								</div>
							)}

							{/* Windowed Content Grid - for all sections */}
							{/* Windowed Content Grid - for all sections */}
							{filteredContent.length > 0 ? (
								viewMode === 'list' ? (
									<div className="content-list-container">
										<ContentListHeader />
										<WindowedContentGrid
											key={activeSection}
											items={filteredContent}
											viewMode={viewMode}
											fetchNextPage={fetchNextPage}
											hasNextPage={hasNextPage}
											isFetchingNextPage={isFetchingNextPage}
											renderCard={(item, localIndex) => {
												return (
													<ContentListRow
														key={`${item.type}-${item.id}`}
														id={item.id}
														name={item.name}
														type={item.type}
														playCount={item.playCount}
														sectionCount={item.sectionCount}
														lastModified={new Date(item.updatedAt).toLocaleDateString()}
														isStarred={item.starred}
														selected={isItemSelected(item.id, item.type)}
														onSelect={toggleSelection}
														onRename={cardCallbacks.handleRename}
														onDelete={cardCallbacks.handleDelete}
														onDuplicate={handleDuplicate}
														onExport={handleExportPlaybook}
														onShare={handleShare}
														onToggleStar={handleToggleStar}
														style={{ '--row-index': localIndex } as React.CSSProperties}
													/>
												)
											}}
										/>
									</div>
								) : (
									<WindowedContentGrid
										key={activeSection}
										items={filteredContent}
										viewMode={viewMode}
										fetchNextPage={fetchNextPage}
										hasNextPage={hasNextPage}
										isFetchingNextPage={isFetchingNextPage}
										renderCard={(item, localIndex) => {
											// Use stable callbacks from the hook - no inline arrow functions
											const commonProps = {
												id: item.id,
												name: item.name,
												type: item.type,
												playCount: item.playCount,
												sectionCount: item.sectionCount,
												lastModified: new Date(item.updatedAt).toLocaleDateString(),
												isStarred: item.starred,
												selected: isItemSelected(item.id, item.type),
												onSelect: toggleSelection,
												onRename: cardCallbacks.handleRename,
												onDelete: cardCallbacks.handleDelete,
												onDuplicate: handleDuplicate,
												onExport: handleExportPlaybook,
												onShare: handleShare,
												onToggleStar: handleToggleStar,
											}

											return (
												<ContentCard
													key={`${item.type}-${item.id}`}
													{...commonProps}
													previewPlays={item.previewPlays}
													previewDocument={item.previewDocument}
													dataAnimate="fade-slide"
													style={{ '--card-index': localIndex } as React.CSSProperties}
												/>
											)
										}}
									/>
								)
							) : (
								<div className="empty-state">
									{selectedFolderId !== null ? (
										<p className="empty-state-text">This folder is empty.</p>
									) : activeSection === 'trash' ? (
										<p className="empty-state-text">Trash is empty.</p>
									) : activeSection === 'starred' ? (
										<p className="empty-state-text">No starred content yet. Star an item to see it here.</p>
									) : activeSection === 'recent' ? (
										<p className="empty-state-text">No recently opened content.</p>
									) : activeSection === 'shared' ? (
										<p className="empty-state-text">No content has been shared with you.</p>
									) : activeSection === 'playbooks' ? (
										<>
											<p className="empty-state-text">No playbooks found</p>
											<button
												onClick={() => modals.open('newPlaybook')}
												className="empty-state-button"
											>
												Create Your First Playbook
											</button>
										</>
									) : activeSection === 'documents' ? (
										<>
											<p className="empty-state-text">No documents found</p>
											<button
												onClick={() => modals.open('newDocument')}
												className="empty-state-button"
											>
												Create Your First Document
											</button>
										</>
									) : (
										<p className="empty-state-text">No content found.</p>
									)}
								</div>
							)}
						</div>

						{/* Selection Action Bar */}
						{selectedItems.size > 0 && (
							<SelectionActionBar
								selectedCount={selectedItems.size}
								onClearSelection={clearSelection}
								actions={
									activeSection === 'trash'
										? [
											{
												label: 'Restore',
												icon: <RotateCcw size={18} />,
												onClick: handleBatchRestore,
											},
											{
												label: 'Delete Permanently',
												icon: <Trash2 size={18} />,
												onClick: handleBatchPermanentDelete,
												variant: 'destructive' as const,
											},
										]
										: selectedFolderId !== null
											? [
												{
													label: 'Remove from Folder',
													icon: <FolderMinus size={18} />,
													onClick: handleRemoveFromFolder,
												},
												{
													label: 'Delete',
													icon: <Trash2 size={18} />,
													onClick: handleBatchDelete,
													variant: 'destructive' as const,
												},
											]
											: [
												{
													label: 'Add to Folder',
													icon: <FolderPlus size={18} />,
													onClick: () => modals.open('addToFolder'),
												},
												{
													label: 'Delete',
													icon: <Trash2 size={18} />,
													onClick: handleBatchDelete,
													variant: 'destructive' as const,
												},
											]
								}
							/>
						)}
					</div>
					</ErrorBoundary>
				</ResizablePanel>
			</ResizablePanelGroup>

			{/* Modals outside the resizable panels */}
			<div className="content-manager-modals">
				{/* New Playbook Dialog */}
				<NewPlaybookDialog
					isOpen={modals.isOpen('newPlaybook')}
					onClose={() => modals.close('newPlaybook')}
					onCreatePlaybook={handleCreatePlaybook}
				/>

				{/* New Document Dialog */}
				<NewDocumentDialog
					isOpen={modals.isOpen('newDocument')}
					onClose={() => modals.close('newDocument')}
					onCreateDocument={handleCreateDocument}
					playbooks={playbooks}
				/>

				{/* Settings Dialog */}
				<UnifiedSettingsDialog
					isOpen={modals.isOpen('settings')}
					onClose={() => modals.close('settings')}
					context="content-manager"
				/>

				{/* Manage Teams Dialog */}
				<ManageTeamsDialog
					isOpen={modals.isOpen('manageTeams')}
					onClose={() => modals.close('manageTeams')}
				/>

				{/* New Folder Dialog */}
				<NewFolderDialog
					isOpen={modals.isOpen('newFolder')}
					onClose={() => modals.close('newFolder')}
				/>

				{/* Add to Folder Dialog */}
				<AddToFolderDialog
					isOpen={modals.isOpen('addToFolder')}
					onClose={() => modals.close('addToFolder')}
					selectedItems={Array.from(selectedItems).map(key => {
						const [type, idStr] = key.split(':') as [DbContentType, string]
						return { type, id: idStr }  // Keep as string - blocks use UUIDs
					})}
					onAddToFolders={handleAddToFolders}
				/>

				{/* Share Playbook Dialog */}
				{shareTarget && (
					<PlaybookShareDialog
						isOpen={modals.isOpen('share')}
						onClose={() => {
							modals.close('share')
							setShareTarget(null)
						}}
						playbookId={shareTarget.id}
						playbookName={shareTarget.name}
						currentTeamId={shareTarget.teamId}
					/>
				)}

				{/* Rename Dialog */}
				<RenameDialog
					isOpen={modals.isOpen('rename')}
					target={renameTarget}
					onClose={() => {
						modals.close('rename')
						setRenameTarget(null)
					}}
					onRename={handleRename}
				/>

				{/* Delete Confirmation Dialog */}
				<Dialog open={confirmations.deleteConfirm.isOpen} onOpenChange={(open) => !open && confirmations.deleteConfirm.cancel()}>
					<DialogContent hideClose>
						<DialogHeader>
							<DialogTitle>{confirmations.deleteConfirm.config.title}</DialogTitle>
							<DialogDescription>
								{confirmations.deleteConfirm.getDescription()}
							</DialogDescription>
						</DialogHeader>
						<DialogFooter>
							<Button variant="outline" onClick={confirmations.deleteConfirm.cancel}>Cancel</Button>
							<Button variant="destructive" onClick={confirmations.deleteConfirm.confirm}
									disabled={confirmations.deleteConfirm.isConfirming}>
								{confirmations.deleteConfirm.isConfirming ? 'Deleting...' : 'Delete'}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Permanent Delete Confirmation Dialog */}
				<Dialog open={confirmations.permanentDeleteConfirm.isOpen}
						onOpenChange={(open) => !open && confirmations.permanentDeleteConfirm.cancel()}>
					<DialogContent hideClose>
						<DialogHeader>
							<DialogTitle>{confirmations.permanentDeleteConfirm.config.title}</DialogTitle>
							<DialogDescription>
								{confirmations.permanentDeleteConfirm.getDescription()}
							</DialogDescription>
						</DialogHeader>
						<DialogFooter>
							<Button variant="outline" onClick={confirmations.permanentDeleteConfirm.cancel}>Cancel</Button>
							<Button variant="destructive" onClick={confirmations.permanentDeleteConfirm.confirm}
									disabled={confirmations.permanentDeleteConfirm.isConfirming}>
								{confirmations.permanentDeleteConfirm.isConfirming ? 'Deleting...' : 'Permanently Delete'}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Restore Confirmation Dialog */}
				<Dialog open={confirmations.restoreConfirm.isOpen} onOpenChange={(open) => !open && confirmations.restoreConfirm.cancel()}>
					<DialogContent hideClose>
						<DialogHeader>
							<DialogTitle>{confirmations.restoreConfirm.config.title}</DialogTitle>
							<DialogDescription>
								{confirmations.restoreConfirm.getDescription()}
							</DialogDescription>
						</DialogHeader>
						<DialogFooter>
							<Button variant="outline" onClick={confirmations.restoreConfirm.cancel}>Cancel</Button>
							<Button variant="default" onClick={confirmations.restoreConfirm.confirm}
									disabled={confirmations.restoreConfirm.isConfirming}>
								{confirmations.restoreConfirm.isConfirming ? 'Restoring...' : 'Restore'}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Empty Trash Confirmation Dialog */}
				<Dialog open={confirmations.emptyTrashConfirm.isOpen} onOpenChange={(open) => !open && confirmations.emptyTrashConfirm.cancel()}>
					<DialogContent hideClose>
						<DialogHeader>
							<DialogTitle>{confirmations.emptyTrashConfirm.config.title}</DialogTitle>
							<DialogDescription>
								{confirmations.emptyTrashConfirm.getDescription()}
							</DialogDescription>
						</DialogHeader>
						<DialogFooter>
							<Button variant="outline" onClick={confirmations.emptyTrashConfirm.cancel}>Cancel</Button>
							<Button variant="destructive" onClick={confirmations.emptyTrashConfirm.confirm}
									disabled={confirmations.emptyTrashConfirm.isConfirming}>
								{confirmations.emptyTrashConfirm.isConfirming ? 'Emptying...' : 'Empty Trash'}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Delete Folder Confirmation Dialog */}
				<Dialog open={confirmations.deleteFolderConfirm.isOpen}
						onOpenChange={(open) => !open && confirmations.deleteFolderConfirm.cancel()}>
					<DialogContent hideClose>
						<DialogHeader>
							<DialogTitle>{confirmations.deleteFolderConfirm.config.title}</DialogTitle>
							<DialogDescription>
								{confirmations.deleteFolderConfirm.getDescription()}
							</DialogDescription>
						</DialogHeader>
						<DialogFooter>
							<Button variant="outline" onClick={confirmations.deleteFolderConfirm.cancel}>Cancel</Button>
							<Button variant="destructive" onClick={confirmations.deleteFolderConfirm.confirm}
									disabled={confirmations.deleteFolderConfirm.isConfirming}>
								{confirmations.deleteFolderConfirm.isConfirming ? 'Deleting...' : 'Delete'}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Onboarding Tour */}
				<TourController />
				<WelcomeModal
					isOpen={showWelcome}
					onStartTour={() => {
						markWelcomeSeen()
						startTour('content-manager')
					}}
					onDismiss={markWelcomeSeen}
				/>
			</div>
		</>
	)
}
